/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { hideElementOnPrint } from "@rc/App/SharedStyles/sharedStyles";
import { CustomAppState, xHeaderActions } from "@rc/App/Store/Reducers";
import { screen } from "@rc/XDL/Helpers";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface LogoLinksSearchStyles {
    logoWrapper: InjectableCss;
    searchWrapper?: InjectableCss;
    containerContentWrapper?: InjectableCss;
    navigationWrapper?: InjectableCss;
    menuTriggerButton?: ClickablePresentationProps;
    menuIcon?: IconPresentationProps;
    searchMenuWrapper?: InjectableCss;
    secondaryNavigationWrapper?: InjectableCss;
    contactNavigationWrapper?: InjectableCss;
    accountMenuWrapper?: InjectableCss;
    cartLinkWrapper?: InjectableCss;
}

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    containerContentWrapper: {
        css: css`
            position: relative;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-top: 12px;
            padding-bottom: 10px;
            height: 66px;
            ${screen("lte", "md")`
                height: 60px;
            `}

            ${({ isCheckoutPages }: any) => {
                if (isCheckoutPages) {
                    return `
                    justify-content: center;
                    `;
                }
                return `

                `;
            }}
        `,
    },
    logoWrapper: {
        css: css`
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            text-align: center;
            min-width: 5px;
            height: 44px;
            width: 121px;
            margin-right: 10px;
            margin-left: 10px;
            display: flex;
            align-items: center;
            top: 15px;

            .wrapper {
                display: flex;
                align-items: center;
                a {
                    line-height: 1;
                }
            }

            ${screen("lte", "md")`
                height: 36px;
                width: 100px;
            `}

            ${({ searchInputFocus }: any) => searchInputFocus && `width:0;max-width:0;height:0;visibility:hidden;`};
        `,
    },
    menuTriggerButton: {
        css: css`
            ${({ searchInputFocus }: any) => searchInputFocus && `width:0;max-width:0;height:0;visibility:hidden;`};
        `,
    },
    navigationWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-end;
            ${({ searchInputFocus }: any) => searchInputFocus && `width:0;height:0;visibility:hidden;`};
            ${hideElementOnPrint}
        `,
    },
    searchWrapper: {
        css: css`
            width: 100%;
            max-width: 308px;
            transition: 0.2s;
            &.searchInputFocus {
                max-width: 100%;
            }
            ${screen("lte", "md")`
                max-width: 20px;
                &.searchInputFocus {
                    max-width: 100%;
                }
            `};
            ${hideElementOnPrint}
        `,
    },
    secondaryNavigationWrapper: {
        css: css`
            margin-right: 35px;

            ${screen("lte", "md")`
                display: none;
            `};
        `,
    },
    contactNavigationWrapper: {
        css: css`
            margin-right: 35px;

            ${screen("lte", "md")`
                display: none;
            `};
        `,
    },
    accountMenuWrapper: {
        css: css`
            margin-right: 35px;

            ${screen("lte", "md")`
                margin-right: 16px;
            `};
        `,
    },
    searchMenuWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-start;
            ${({ searchInputFocus }: any) => searchInputFocus && `width:100%;max-width: 100%;`};
        `,
    },
};

const mapStateToProps = (state: CustomAppState) => {
    const pageType: any = getCurrentPage(state).type;

    return {
        searchInputFocus: state.xHeader.searchInputFocus,
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,

        isCheckoutPages:
            pageType === "CheckoutShippingPage" ||
            pageType === "CheckoutReviewAndSubmitPage" ||
            pageType === "OrderConfirmationPage" ||
            pageType === "RfqRequestQuotePage" ||
            pageType === "RfqConfirmationPage",
    };
};

const mapDispatchToProps = {
    setNavDrawerIsOpen,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const StyledWrapperX = getStyledWrapper("div") as any; // typeof StyledWrapper;

const LogoLinksSearch: FC<Props> = ({ id, searchInputFocus, isCheckoutPages, navDrawerIsOpen, setNavDrawerIsOpen }) => {
    const openDrawer = () => {
        setNavDrawerIsOpen({ navDrawerIsOpen: true });
    };

    const closeDrawer = () => {
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
    };
    return (
        <StyledWrapperX
            {...logoLinksSearchStyles.containerContentWrapper}
            {...({ isCheckoutPages } as any)}
            className="header_logo-links-search"
        >
            {!isCheckoutPages && (
                <StyledWrapperX searchInputFocus={searchInputFocus} {...logoLinksSearchStyles.searchMenuWrapper}>
                    <Hidden
                        above="md"
                        css={css`
                            @media print {
                                display: none;
                            }
                        `}
                    >
                        {navDrawerIsOpen ? (
                            <Clickable
                                onClick={closeDrawer}
                                {...{ searchInputFocus }}
                                {...logoLinksSearchStyles.menuTriggerButton}
                                data-test-selector="expandMobileMenu"
                            >
                                <Icon
                                    size={20}
                                    src="Close"
                                    color="common.ui10"
                                    {...logoLinksSearchStyles.menuIcon}
                                    key="close-menu-icon"
                                />
                                <VisuallyHidden>{translate("close menu")}</VisuallyHidden>
                            </Clickable>
                        ) : (
                            <Clickable
                                onClick={openDrawer}
                                {...{ searchInputFocus }}
                                {...logoLinksSearchStyles.menuTriggerButton}
                                data-test-selector="expandMobileMenu"
                            >
                                <Icon
                                    size={25}
                                    src="MenuInactiveInactive"
                                    color="common.ui10"
                                    {...logoLinksSearchStyles.menuIcon}
                                    key="open-menu-icon"
                                />
                                <VisuallyHidden>{translate("open menu")}</VisuallyHidden>
                            </Clickable>
                        )}
                    </Hidden>
                    <StyledWrapperX
                        searchInputFocus={searchInputFocus}
                        {...logoLinksSearchStyles.searchWrapper}
                        className={searchInputFocus ? " header_search searchInputFocus" : "header_search"}
                    >
                        <Zone contentId={id} zoneName="SearchZone" />
                    </StyledWrapperX>
                </StyledWrapperX>
            )}
            <StyledWrapperX {...logoLinksSearchStyles.logoWrapper} searchInputFocus={searchInputFocus}>
                <Zone contentId={id} zoneName="LogoZone" />
            </StyledWrapperX>
            {!isCheckoutPages && (
                <StyledWrapperX searchInputFocus={searchInputFocus} {...logoLinksSearchStyles.navigationWrapper}>
                    <StyledWrapperX {...logoLinksSearchStyles.secondaryNavigationWrapper}>
                        <Zone contentId={id} zoneName="SecondaryNavigation" />
                    </StyledWrapperX>
                    <StyledWrapperX {...logoLinksSearchStyles.contactNavigationWrapper}>
                        <Zone contentId={id} zoneName="ContactNavigation" />
                    </StyledWrapperX>
                    <StyledWrapperX {...logoLinksSearchStyles.accountMenuWrapper}>
                        <Zone contentId={id} zoneName="accountMenu" />
                    </StyledWrapperX>
                    <StyledWrapperX {...logoLinksSearchStyles.cartLinkWrapper}>
                        <Zone contentId={id} zoneName="cartLink" />
                    </StyledWrapperX>
                </StyledWrapperX>
            )}
        </StyledWrapperX>
    );
};

const logoLinksSearch: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LogoLinksSearch),
    definition: {
        group: "Common",
        icon: "magnifying-glass",
    },
};

export default logoLinksSearch;
